import { useMemo, useRef, useState } from 'react';

/**
 * Gráficos em SVG puro.
 * Paleta categórica de ordem fixa (nunca ciclada) validada para daltonismo
 * e contraste sobre as duas superfícies do painel.
 */
export const CATEGORICAL = ['#a560f6', '#396ba8', '#2aa5a6', '#be7907', '#c0204b'] as const;
const GRID = 'rgba(255,255,255,0.07)';
const AXIS_TEXT = '#767d93';

export interface Series {
  key: string;
  label: string;
  values: number[];
  format?: (value: number) => string;
}

interface TooltipState {
  x: number;
  y: number;
  content: { title: string; rows: { label: string; value: string; color: string }[] };
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

function Tooltip({ state }: { state: TooltipState | null }): JSX.Element | null {
  if (!state) return null;
  return (
    <div className="chart-tooltip" style={{ left: state.x, top: state.y, transform: 'translate(-50%, -115%)' }}>
      <strong style={{ display: 'block', marginBottom: 4 }}>{state.content.title}</strong>
      {state.content.rows.map((row) => (
        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="dot" style={{ background: row.color }} />
          <span style={{ color: 'var(--text-3)' }}>{row.label}</span>
          <b style={{ marginLeft: 'auto', paddingLeft: 10 }}>{row.value}</b>
        </div>
      ))}
    </div>
  );
}

export function Legend({ series }: { series: { label: string; color: string }[] }): JSX.Element {
  return (
    <div className="chart-legend">
      {series.map((item) => (
        <span key={item.label}>
          <i className="dot" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Linha / área temporal                                               */
/* ------------------------------------------------------------------ */

export function LineChart({
  labels,
  series,
  height = 230,
  area = false,
  valueFormat = (v: number) => String(Math.round(v)),
}: {
  labels: string[];
  series: Series[];
  height?: number;
  area?: boolean;
  valueFormat?: (value: number) => string;
}): JSX.Element {
  const wrap = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const width = 720;
  const padding = { top: 14, right: 16, bottom: 26, left: 46 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const max = useMemo(
    () => niceMax(Math.max(1, ...series.flatMap((s) => s.values))),
    [series],
  );
  const stepX = labels.length > 1 ? innerW / (labels.length - 1) : 0;
  const xAt = (i: number): number => padding.left + i * stepX;
  const yAt = (value: number): number => padding.top + innerH - (value / max) * innerH;

  if (!labels.length) {
    return <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>Sem dados no período selecionado.</p>;
  }

  const gridLines = [0, 0.25, 0.5, 0.75, 1];
  const tickEvery = Math.max(1, Math.ceil(labels.length / 7));

  function handleMove(event: React.MouseEvent<SVGSVGElement>): void {
    const rect = event.currentTarget.getBoundingClientRect();
    const relative = ((event.clientX - rect.left) / rect.width) * width;
    const index = Math.round((relative - padding.left) / (stepX || 1));
    if (index < 0 || index >= labels.length) return;
    setHoverIndex(index);
    const wrapRect = wrap.current?.getBoundingClientRect();
    setTooltip({
      x: wrapRect ? ((xAt(index) / width) * rect.width) : 0,
      y: wrapRect ? ((yAt(Math.max(...series.map((s) => s.values[index] ?? 0))) / height) * rect.height) : 0,
      content: {
        title: labels[index] ?? '',
        rows: series.map((s, i) => ({
          label: s.label,
          value: (s.format ?? valueFormat)(s.values[index] ?? 0),
          color: CATEGORICAL[i % CATEGORICAL.length]!,
        })),
      },
    });
  }

  return (
    <div className="chart-wrap" ref={wrap}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={`Série temporal: ${series.map((s) => s.label).join(', ')}`}
        onMouseMove={handleMove}
        onMouseLeave={() => { setTooltip(null); setHoverIndex(null); }}
      >
        {gridLines.map((ratio) => (
          <g key={ratio}>
            <line
              x1={padding.left} x2={width - padding.right}
              y1={padding.top + innerH * ratio} y2={padding.top + innerH * ratio}
              stroke={GRID} strokeWidth={1}
            />
            <text
              x={padding.left - 8} y={padding.top + innerH * ratio + 4}
              textAnchor="end" fontSize={11} fill={AXIS_TEXT}
            >
              {valueFormat(max * (1 - ratio))}
            </text>
          </g>
        ))}

        {labels.map((label, index) =>
          index % tickEvery === 0 ? (
            <text key={label + index} x={xAt(index)} y={height - 8} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>
              {label}
            </text>
          ) : null,
        )}

        {hoverIndex !== null && (
          <line x1={xAt(hoverIndex)} x2={xAt(hoverIndex)} y1={padding.top} y2={padding.top + innerH} stroke="rgba(255,255,255,0.22)" strokeWidth={1} />
        )}

        {series.map((s, i) => {
          const color = CATEGORICAL[i % CATEGORICAL.length]!;
          const points = s.values.map((value, index) => `${xAt(index)},${yAt(value)}`).join(' ');
          return (
            <g key={s.key}>
              {area && (
                <polygon
                  points={`${padding.left},${padding.top + innerH} ${points} ${xAt(s.values.length - 1)},${padding.top + innerH}`}
                  fill={color}
                  opacity={0.14}
                />
              )}
              <polyline points={points} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {hoverIndex !== null && (
                <circle cx={xAt(hoverIndex)} cy={yAt(s.values[hoverIndex] ?? 0)} r={4.5} fill={color} stroke="var(--surface)" strokeWidth={2} />
              )}
              {/* rótulo direto no último ponto — identidade não depende só da cor */}
              {series.length <= 4 && s.values.length > 0 && (
                <text
                  x={xAt(s.values.length - 1) + 6}
                  y={yAt(s.values[s.values.length - 1] ?? 0) + 4}
                  fontSize={11}
                  fill={AXIS_TEXT}
                >
                  {s.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Tooltip state={tooltip} />
      {series.length >= 2 && (
        <Legend series={series.map((s, i) => ({ label: s.label, color: CATEGORICAL[i % CATEGORICAL.length]! }))} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Curva de retenção                                                    */
/* ------------------------------------------------------------------ */

export function RetentionChart({
  points,
  markers = [],
  height = 220,
}: {
  points: { percent: number; rate: number }[];
  markers?: { percent: number; label: string }[];
  height?: number;
}): JSX.Element {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const width = 720;
  const padding = { top: 14, right: 16, bottom: 28, left: 44 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  if (!points.length) return <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Sem sessões suficientes.</p>;

  const xAt = (percent: number): number => padding.left + (percent / 100) * innerW;
  const yAt = (rate: number): number => padding.top + innerH - rate * innerH;
  const path = points.map((p) => `${xAt(p.percent)},${yAt(p.rate)}`).join(' ');

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label="Curva de retenção do vídeo"
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const relative = ((event.clientX - rect.left) / rect.width) * width;
          const percent = Math.min(100, Math.max(0, ((relative - padding.left) / innerW) * 100));
          const nearest = points.reduce((a, b) => (Math.abs(b.percent - percent) < Math.abs(a.percent - percent) ? b : a));
          setTooltip({
            x: (xAt(nearest.percent) / width) * rect.width,
            y: (yAt(nearest.rate) / height) * rect.height,
            content: {
              title: `${nearest.percent}% do vídeo`,
              rows: [{ label: 'Ainda assistindo', value: `${(nearest.rate * 100).toFixed(1)}%`, color: CATEGORICAL[0] }],
            },
          });
        }}
        onMouseLeave={() => setTooltip(null)}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
          <g key={ratio}>
            <line x1={padding.left} x2={width - padding.right} y1={padding.top + innerH * ratio} y2={padding.top + innerH * ratio} stroke={GRID} />
            <text x={padding.left - 8} y={padding.top + innerH * ratio + 4} textAnchor="end" fontSize={11} fill={AXIS_TEXT}>
              {Math.round((1 - ratio) * 100)}%
            </text>
          </g>
        ))}
        {[0, 25, 50, 75, 100].map((percent) => (
          <text key={percent} x={xAt(percent)} y={height - 8} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>
            {percent}%
          </text>
        ))}
        <polygon points={`${padding.left},${padding.top + innerH} ${path} ${xAt(100)},${padding.top + innerH}`} fill={CATEGORICAL[0]} opacity={0.16} />
        <polyline points={path} fill="none" stroke={CATEGORICAL[0]} strokeWidth={2} strokeLinejoin="round" />
        {markers.map((marker) => (
          <g key={`${marker.percent}-${marker.label}`}>
            <line x1={xAt(marker.percent)} x2={xAt(marker.percent)} y1={padding.top} y2={padding.top + innerH} stroke={CATEGORICAL[3]} strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={xAt(marker.percent) + 4} y={padding.top + 12} fontSize={10.5} fill={CATEGORICAL[3]}>
              {marker.label}
            </text>
          </g>
        ))}
      </svg>
      <Tooltip state={tooltip} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Barras horizontais (breakdowns)                                     */
/* ------------------------------------------------------------------ */

export function BarChart({
  rows,
  valueFormat = (v: number) => String(Math.round(v)),
  colorIndex = 0,
  emptyLabel = 'Sem dados.',
}: {
  rows: { label: string; value: number; meta?: string }[];
  valueFormat?: (value: number) => string;
  colorIndex?: number;
  emptyLabel?: string;
}): JSX.Element {
  if (!rows.length) return <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{emptyLabel}</p>;
  const max = Math.max(...rows.map((r) => r.value), 1);
  const color = CATEGORICAL[colorIndex % CATEGORICAL.length]!;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((row) => (
        <div key={row.label} title={`${row.label}: ${valueFormat(row.value)}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, marginBottom: 4 }}>
            <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.label}
            </span>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>
              {valueFormat(row.value)}
              {row.meta ? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {row.meta}</span> : null}
            </b>
          </div>
          <div style={{ height: 8, background: 'var(--surface-3)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(row.value / max) * 100}%`, height: '100%', background: color, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Funil                                                                */
/* ------------------------------------------------------------------ */

export function FunnelChart({ steps }: { steps: { label: string; value: number }[] }): JSX.Element {
  if (!steps.length) return <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Sem eventos ainda.</p>;
  const top = Math.max(steps[0]?.value ?? 1, 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {steps.map((step, index) => {
        const ratio = step.value / top;
        const previous = index > 0 ? steps[index - 1]!.value : step.value;
        const drop = previous > 0 ? 1 - step.value / previous : 0;
        return (
          <div key={step.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
              <span style={{ color: 'var(--text-2)' }}>{step.label}</span>
              <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                {new Intl.NumberFormat('pt-BR').format(step.value)}
                {index > 0 && drop > 0 && (
                  <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · −{(drop * 100).toFixed(0)}%</span>
                )}
              </b>
            </div>
            <div style={{ height: 22, background: 'var(--surface-3)', borderRadius: 6, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${Math.max(ratio * 100, step.value > 0 ? 2 : 0)}%`,
                  height: '100%',
                  borderRadius: 6,
                  background: CATEGORICAL[index % CATEGORICAL.length],
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
